import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { gql } from 'graphql-request'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Clock, Loader2 } from 'lucide-react'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { useTranslation } from '@/hooks'
import { formatMinutes, formatPrice } from '@/pages/Module2/MinutesOfferModal'
import { formatDay, formatPlanPrice, subscriptionStore } from '@/pages/Module2/subscriptionStore'

export interface MyPurchase {
  id: number
  moduleName: string | null
  packageName: string
  amount: number
  currency: string
  status: string
  refundedAmount: number
  createdAt: string
}

export interface MyMinutesOverview {
  metered: boolean
  purchases: MyPurchase[]
}

export const GET_MY_MINUTES = gql`
  query getMyMinutesOverview {
    getMyMinutesOverview {
      metered
      purchases {
        id
        moduleName
        packageName
        amount
        currency
        status
        refundedAmount
        createdAt
      }
    }
  }
`

const formatDate = (value: string | null, language: string): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' })
}

/**
 * The B2C user's subscription and minutes: the plan and when it renews or ends, a way
 * to manage it (Stripe's portal) or to choose one, all their minutes as one total, any
 * minutes their plan does not cover, and what they have bought. Read-only; nothing here
 * changes a balance.
 */
const MinutesTab: React.FC<{ overview?: MyMinutesOverview | null }> = ({ overview: provided }) => {
  const { t, currentLanguage } = useTranslation()
  const [overview, setOverview] = useState<MyMinutesOverview | null>(provided ?? null)
  const [loading, setLoading] = useState(!provided)

  useEffect(() => {
    subscriptionStore.refresh()
  }, [])

  useEffect(() => {
    if (provided) {
      setOverview(provided)
      setLoading(false)
      return
    }
    let cancelled = false
    authorizedGraphQLRequest<{ getMyMinutesOverview: MyMinutesOverview }>(GET_MY_MINUTES)
      .then((response) => {
        if (!cancelled) setOverview(response.getMyMinutesOverview)
      })
      .catch((error) => console.error('Failed to load your minutes:', error))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [provided])

  if (loading || !subscriptionStore.loaded) {
    return (
      <div className="mt-8 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    )
  }

  const mine = subscriptionStore.mine
  const subscription = mine?.subscription
  const plan = mine?.plan
  const periodEnd = formatDate(subscription?.currentPeriodEnd ?? null, currentLanguage)
  const purchases = overview?.purchases ?? []

  return (
    <Card className="border-none p-0 shadow-none mt-4">
      <CardHeader className="p-0 mb-6">
        <CardTitle className="text-2xl font-bold text-gray-800">{t('settings.minutes.title')}</CardTitle>
        <CardDescription className="text-gray-600 mt-2">{t('settings.minutes.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-8 p-0">
        <div className="grid gap-4 md:grid-cols-2">
          {/* The subscription. */}
          <div className="p-5 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl shadow-sm border-l-4 border-primary">
            <div className="text-sm font-semibold text-primary mb-2">{t('subscription.settings.title')}</div>
            {mine?.hasAccess && plan ? (
              <>
                <div className="text-lg font-bold text-gray-800">
                  {t('subscription.settings.plan', {
                    plan: plan.name,
                    price: formatPlanPrice(plan, currentLanguage),
                    interval: t(`subscription.paywall.${plan.renews ? 'per' : 'forOne'}.${plan.interval}`),
                  })}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  {subscription?.status === 'past_due'
                    ? t('subscription.settings.pastDue')
                    : subscription?.cancelAtPeriodEnd
                      ? t('subscription.settings.ends', { date: periodEnd })
                      : t('subscription.settings.renews', { date: periodEnd, count: formatMinutes(plan.includedSeconds) })}
                </div>
                <button
                  type="button"
                  disabled={subscriptionStore.openingPortal}
                  onClick={() => subscriptionStore.openPortal()}
                  className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary underline underline-offset-2 disabled:opacity-60"
                >
                  {subscriptionStore.openingPortal && <Loader2 className="h-3 w-3 animate-spin" />}
                  {t('subscription.settings.manage')}
                </button>
              </>
            ) : (
              <>
                <div className="text-gray-700">{t('subscription.settings.none')}</div>
                <a href="/module2" className="mt-3 inline-block text-sm font-semibold text-primary underline underline-offset-2">
                  {t('subscription.settings.choosePlan')}
                </a>
              </>
            )}
          </div>

          {/* All the minutes, as the sidebar shows them. */}
          <div className="p-5 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl shadow-sm border-l-4 border-primary">
            <div className="flex items-center gap-2 text-sm font-semibold text-primary mb-2">
              <Clock className="h-4 w-4" />
              <span>{t('subscription.settings.total')}</span>
            </div>
            <div className="text-xl font-bold text-gray-800 tabular-nums">{t('subscription.sidebar.minutes', { count: formatMinutes(mine?.totalSeconds ?? 0) })}</div>
            <div className="text-xs text-gray-600 mt-1">
              {mine?.nextExpiry ? t('subscription.settings.nextExpiry', { date: formatDate(mine.nextExpiry, currentLanguage) }) : t('settings.minutes.nothingLeft')}
            </div>
            {mine?.hasAccess && (
              <a href="/module2?buy=1" className="mt-3 inline-block text-sm font-semibold text-primary underline underline-offset-2">
                {t('settings.minutes.buy')}
              </a>
            )}
          </div>
        </div>

        {mine && mine.unusable.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('subscription.settings.unusableTitle')}</h3>
            <ul className="space-y-1 text-sm text-gray-700">
              {mine.unusable.map((u, i) => (
                <li key={i}>
                  {t('subscription.settings.unusableItem', {
                    count: formatMinutes(u.remainingSeconds),
                    modules: u.moduleNames.join(', '),
                    date: formatDay(u.expiresAt, currentLanguage),
                  })}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('settings.minutes.history')}</h3>
          {purchases.length === 0 ? (
            <p className="text-gray-600">{t('settings.minutes.noPurchases')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-2 pr-4 font-semibold">{t('settings.minutes.date')}</th>
                    <th className="py-2 pr-4 font-semibold">{t('settings.minutes.package')}</th>
                    <th className="py-2 pr-4 font-semibold">{t('settings.minutes.amount')}</th>
                    <th className="py-2 font-semibold">{t('settings.minutes.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.map((purchase) => (
                    <tr key={purchase.id} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 pr-4 text-gray-700">{formatDate(purchase.createdAt, currentLanguage)}</td>
                      <td className="py-2 pr-4 text-gray-700">
                        {purchase.packageName}
                        {/* Per-module packages from before subscriptions name their module. */}
                        {purchase.moduleName && <span className="text-gray-500"> · {purchase.moduleName}</span>}
                      </td>
                      <td className="py-2 pr-4 tabular-nums text-gray-700">{formatPrice(purchase.amount, purchase.currency, currentLanguage)}</td>
                      <td className="py-2 text-gray-700">
                        {purchase.refundedAmount > 0 ? t('settings.minutes.statuses.refunded') : t(`settings.minutes.statuses.${purchase.status}`, purchase.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default observer(MinutesTab)
